import React from "react";
import { Stack, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Cue,
  Player,
  ReverseRoundResultsPayload,
  ReverseRoundSequenceCard,
} from "../../shared/interfaces/GameLogicTypes";
import useSocketContext from "../../shared/useSocketContext";
import SequenceBar from "../SequenceBar/SequenceBar";
import "./ReverseRoundResults.css";

export interface ReverseRoundResultsProps {
  results: ReverseRoundResultsPayload;
  cues: Cue[];
  players: { [id: string]: Player };
  waitForHost: boolean;
  isHost: boolean;
  isReady: boolean;
  readyPlayerIds: string[];
  totalPlayers: number;
  onReadyToggle: (next: boolean) => void;
}

export default function ReverseRoundResults({
  results,
  cues,
  players,
  waitForHost,
  isHost,
  isReady,
  readyPlayerIds,
  totalPlayers,
  onReadyToggle,
}: ReverseRoundResultsProps) {
  const { t } = useTranslation();
  const socket = useSocketContext().socket;

  // Build player name lookup from sequences + players prop
  const nameOf = (id: string): string => {
    if (players[id]) return players[id].name;
    for (const seq of results.sequences) {
      if (seq.submittedBy === id) return seq.submitterName;
      for (const g of seq.guesses) {
        if (g.playerId === id) return g.playerName;
      }
    }
    return id;
  };

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Typography variant="h6" textAlign="center" sx={{ fontWeight: 600 }}>
        {t("rr_mode_label", "Reverse Round")}
      </Typography>

      <div className="rr-results">
        <div className="rr-results-scroll">
          {results.sequences.map((card, idx) => (
            <SequenceCard key={idx} card={card} cues={cues} t={t} />
          ))}
        </div>
      </div>

      {/* Ready / Continue controls — mirrors FtlStreakResults pattern */}
      {waitForHost ? (
        isHost ? (
          <Button
            variant="contained"
            onClick={() => socket.emit("nextTurn")}
            sx={{ alignSelf: "center" }}
          >
            {t("continue", "Continue")}
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t("waiting_for_host", "Waiting for host...")}
          </Typography>
        )
      ) : (
        <Button
          variant={isReady ? "outlined" : "contained"}
          onClick={() => onReadyToggle(!isReady)}
          sx={{ alignSelf: "center" }}
        >
          {isReady ? t("ready", "Ready") : t("continue", "Continue")} ({readyPlayerIds.length}/{totalPlayers})
        </Button>
      )}
    </Stack>
  );
}

function SequenceCard({
  card,
  cues,
  t,
}: {
  card: ReverseRoundSequenceCard;
  cues: Cue[];
  t: any;
}) {
  const cardClass = `rr-sequence-card${card.isRandom ? " random" : ""}`;
  const headerClass = `rr-card-header${card.isRandom ? " random" : ""}`;

  return (
    <div className={cardClass}>
      <div className={headerClass}>
        {card.isRandom
          ? t("rr_random_sequence", "Random Sequence")
          : t("rr_submitted_by", { name: card.submitterName, defaultValue: `Submitted by ${card.submitterName}` })}
      </div>

      <SequenceBar
        readOnly
        sequence={card.sequence}
        cue1={cues[0] ?? { content: "#1" }} cue2={cues[1] ?? { content: "#2" }}
        cue3={cues[2] ?? { content: "#3" }} cue4={cues[3] ?? { content: "#4" }}
        cue5={cues[4] ?? { content: "#5" }}
        reset={() => {}} canSkip={false} decreaseRemainingSkips={() => {}} remainingSkips={0}
      />

      {card.guesses.map((guess, idx) => {
        const icon = guess.correct ? "\u2713" : "\u2717";
        const className = `rr-card-guess ${guess.correct ? "correct" : "wrong"}`;
        return (
          <div key={idx} className={className}>
            <span className="rr-card-guess-icon">{icon}</span>
            <span>{guess.playerName}</span>
            {!guess.correct && (
              <span style={{ color: "#777", fontSize: "0.75rem" }}>
                {" "}({guess.guessedName
                  ? t("rr_thought_it_was", { name: guess.guessedName, defaultValue: `thought it was ${guess.guessedName}` })
                  : t("rr_no_answer", "no answer")})
              </span>
            )}
            <span className="rr-card-points">
              {guess.points > 0 ? `+${guess.points}` : ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}
